import React from 'react';
import {
  ATMDivider,
  ATMForm,
  ATMGrid,
  ATMHeader,
  ATMSegment,
  ATMTable,
} from 'shared-it-appmod-ui';
import FieldReadOnlyCommment from 'src/components/atoms/field/field-readonly-comment/field-readonly-comment.component';
import FieldReadOnly from 'src/components/atoms/field/field-readonly/field-readonly.component';
import FlagReadOnly from 'src/components/atoms/flag-readonly/flag-readonly.component';
import FileManager from 'src/components/molecules/file-manager/file-manager.component';
import { FileCategory } from 'src/constants';
import Lang from 'src/libraries/language';
import { IRequest } from 'src/models/request.model';
import FlightRequestConstructionLandingZone from '../flight-request-construction-landing-zone/flight-request-construction-landing-zone.component';
import styles from './flight-request-construction.module.scss';

type IProps = {
  data: IRequest;
};

const FlightRequestConstruction: React.FC<IProps> = ({
  data: { constructionFlight },
}) => {
  return (
    <ATMSegment color="orange" className={styles.wrapper}>
      <ATMHeader>{Lang.TTL_REQUEST_CONSTRUCTION}</ATMHeader>

      <ATMForm>
        <ATMGrid columns={2}>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_FLIGHT_DISTRICT_DEPARTMENT}>
              {constructionFlight.flightDept}
            </FieldReadOnly>
          </ATMGrid.Column>
        </ATMGrid>
        {constructionFlight?.purpose && (
          <ATMGrid columns={2}>
            <ATMGrid.Column>
              <FieldReadOnly
                label={Lang.LBL_PURPOSE}
                className={styles.wrapText}
              >
                {constructionFlight.purpose}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid>
        )}
        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_STRUCTURE_IDS}
        </ATMHeader>

        <ATMTable>
          <ATMTable.Header>
            <ATMTable.Row>
              <ATMTable.HeaderCell>{Lang.LBL_STRUCTURE_ID}</ATMTable.HeaderCell>
            </ATMTable.Row>
          </ATMTable.Header>

          <ATMTable.Body>
            {(constructionFlight.structureInfos ?? []).map((value, key) => (
              <ATMTable.Row key={`structure_${key}`}>
                <ATMTable.Cell>{value.structureId}</ATMTable.Cell>
              </ATMTable.Row>
            ))}
          </ATMTable.Body>
        </ATMTable>

        <ATMForm.Field>
          <label>{Lang.NTE_ATTACH_FILE_ASSOCIATED_WITH_STRUCTURES}</label>
          <FileManager
            files={constructionFlight.structureFiles}
            allowedTypes={[FileCategory.Structure]}
            editable={false}
            inline
          />
        </ATMForm.Field>

        <FlightRequestConstructionLandingZone
          data={constructionFlight.landingInfos}
        />

        <ATMHeader size="small" className="text-underline">
          {Lang.TTL_ADMIN_FLIGHT_TYPES}
        </ATMHeader>

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.equipmentFlag}
              label={Lang.LBL_LONG_LINE}
            />

            {constructionFlight.equipmentFlag && (
              <ATMSegment>
                <FieldReadOnly
                  className={styles.inlineField}
                  label={Lang.LBL_DESCRIPTION}
                  inline
                >
                  {constructionFlight.equipmentDescription}
                </FieldReadOnly>
              </ATMSegment>
            )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.poleFlag}
              label={Lang.LBL_POLE_SET}
            />

            {constructionFlight.poleFlag && (
              <ATMSegment>
                <FieldReadOnly
                  className={styles.inlineField}
                  label={Lang.LBL_POLE_COUNT}
                  inline
                >
                  {constructionFlight.poleCount}
                </FieldReadOnly>
              </ATMSegment>
            )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.ropeFlag}
              label={Lang.LBL_SOCK_LINE_PULL}
            />

            {constructionFlight.ropeFlag && (
              <ATMSegment>
                <FieldReadOnly
                  className={styles.inlineField}
                  label={Lang.LBL_ROPE_CONDUCTOR_SIZE}
                  inline
                >
                  {constructionFlight.ropeSize}
                </FieldReadOnly>
              </ATMSegment>
            )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.hecFlag}
              label={Lang.LBL_SHORT_HAUL_HEC}
            />

            {constructionFlight.hecFlag && (
              <ATMSegment>
                <FieldReadOnly
                  className={styles.inlineField}
                  label={Lang.LBL_CREW_PRESENT}
                  inline
                >
                  {constructionFlight.crewCount}
                </FieldReadOnly>
              </ATMSegment>
            )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.hazmatFlag}
              label={Lang.LBL_FLYING_HAZMAT}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.crewFlag}
              label={Lang.LBL_CREW_MOVEMENT}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.basketFlag}
              label={Lang.LBL_SKID_BASKET}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FieldReadOnly
              className={styles.inlineField}
              label={Lang.LBL_HEAVIEST_LOAD_WEIGHT}
              inline
            >
              {constructionFlight.loadWeight}
            </FieldReadOnly>
          </ATMGrid.Column>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_OTHER_INFORMATION}
        </ATMHeader>

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.nightFlag}
              label={Lang.LBL_NIGHT_FLIGHT}
            />
          </ATMGrid.Column>

          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.sensitiveFlag}
              label={Lang.LBL_SENSITIVE_CUSTOMER}
            />

            {constructionFlight.sensitiveFlag &&
              (constructionFlight?.sensitiveFiles ?? []).length > 0 && (
                <FileManager
                  files={constructionFlight?.sensitiveFiles}
                  inline
                  editable={false}
                />
              )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.culturalFlag}
              label={Lang.LBL_CULTURAL_RESTRICTION}
            />

            {constructionFlight.culturalFlag &&
              (constructionFlight?.culturalFiles ?? []).length > 0 && (
                <FileManager
                  files={constructionFlight?.culturalFiles}
                  inline
                  editable={false}
                />
              )}
          </ATMGrid.Column>

          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionFlight.landingFlag}
              label={Lang.LBL_REQUEST_HELP_LANDING_ZONE}
            />
          </ATMGrid.Column>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_FLIGHT_AREA}
        </ATMHeader>
        <ATMForm.Field>
          <FileManager
            files={constructionFlight.flightAreaFiles}
            editable={false}
            inline
          />
        </ATMForm.Field>
        <ATMDivider />

        <ATMHeader size="small" className="text-underline">
          {Lang.TTL_BRIEF_DESCRIPTION}
        </ATMHeader>

        <FieldReadOnlyCommment>
          {constructionFlight.comments}
        </FieldReadOnlyCommment>
      </ATMForm>
    </ATMSegment>
  );
};

export default FlightRequestConstruction;
